import { useState, useRef, useEffect, useMemo } from 'react';
import { DialogSection, DialogVariable } from '@/lib/stores/dialogStore';
import { Node, Edge } from '@xyflow/react';
import { 
  ViewMode, 
  SpeakerMode, 
  ContextMenuState, 
  PendingJumpConnection,
  DialogData 
} from '../types';

export interface UseDialogWizardStateProps {
  initialData?: DialogData | null;
  gameId?: string;
}

export interface DialogWizardState {
  // Dialog metadata
  dialogName: string;
  setDialogName: (name: string) => void;
  dialogKey: string;
  setDialogKey: (key: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  translatable: boolean;
  setTranslatable: (val: boolean) => void;
  startingSection: string | undefined;
  setStartingSection: (section: string | undefined) => void;
  tags: string[];
  setTags: (tags: string[]) => void;
  status: string;
  setStatus: (status: string) => void;
  owner: string | null;
  setOwner: (owner: string | null) => void;
  editPermissions: 'Open' | 'Locked';
  setEditPermissions: (permissions: 'Open' | 'Locked') => void;
  
  // Speaker configuration
  speakerMode: SpeakerMode;
  setSpeakerMode: (mode: SpeakerMode) => void;
  speakerTypeId: string | null;
  setSpeakerTypeId: (id: string | null) => void;
  speakerTemplateIds: string[];
  setSpeakerTemplateIds: (ids: string[]) => void;
  
  // Sections state
  sections: DialogSection[];
  setSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  sectionsVersion: number;
  setSectionsVersion: React.Dispatch<React.SetStateAction<number>>;
  dialogVariables: DialogVariable[];
  setDialogVariables: (vars: DialogVariable[]) => void;
  
  // Flow state
  localFlowSections: DialogSection[];
  setLocalFlowSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  flowInitialized: boolean;
  setFlowInitialized: (val: boolean) => void;
  flowId: string | null;
  setFlowId: (id: string | null) => void;
  flowNodes: Node[];
  setFlowNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  flowEdges: Edge[];
  setFlowEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  flowViewport: { x: number; y: number; zoom: number } | null;
  setFlowViewport: (viewport: { x: number; y: number; zoom: number } | null) => void;
  isLoadingFlow: boolean;
  setIsLoadingFlow: (val: boolean) => void;
  needsFlowCreation: boolean;
  setNeedsFlowCreation: (val: boolean) => void;
  isDataLoaded: boolean;
  setIsDataLoaded: (val: boolean) => void;
  
  // UI state
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentSectionIndex: number;
  setCurrentSectionIndex: (index: number) => void;
  isSubmitting: boolean;
  setIsSubmitting: (val: boolean) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (val: boolean) => void;
  
  // Context menu state
  contextMenu: ContextMenuState;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState>>;
  
  // Dialog jump selector state
  dialogJumpSelectorOpen: boolean;
  setDialogJumpSelectorOpen: (val: boolean) => void;
  pendingJumpConnection: PendingJumpConnection | null;
  setPendingJumpConnection: (conn: PendingJumpConnection | null) => void;
  
  // Refs for accessing latest state
  sectionsRef: React.MutableRefObject<DialogSection[]>;
  localFlowSectionsRef: React.MutableRefObject<DialogSection[]>;
  flowNodesRef: React.MutableRefObject<Node[]>;
  prevViewModeRef: React.MutableRefObject<ViewMode>;
  regenerateFlowFromSectionsRef: React.MutableRefObject<(() => void) | undefined>;
  
  // Computed values
  effectiveGameId: string;
  sectionIdentifiers: string[];
}

/**
 * Custom hook for managing all DialogWizard state
 * Consolidates state declarations into a single hook
 */
export function useDialogWizardState({
  initialData,
  gameId,
}: UseDialogWizardStateProps): DialogWizardState {
  // Dialog metadata state
  const [dialogName, setDialogName] = useState('New Dialog');
  const [dialogKey, setDialogKey] = useState('');
  const [description, setDescription] = useState('');
  const [translatable, setTranslatable] = useState(true);
  const [startingSection, setStartingSection] = useState<string | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [status, setStatus] = useState<string>('published');
  const [owner, setOwner] = useState<string | null>(null);
  const [editPermissions, setEditPermissions] = useState<'Open' | 'Locked'>('Open');
  
  // Speaker configuration state
  const [speakerMode, setSpeakerMode] = useState<SpeakerMode>('none');
  const [speakerTypeId, setSpeakerTypeId] = useState<string | null>(null);
  const [speakerTemplateIds, setSpeakerTemplateIds] = useState<string[]>([]);
  
  // Sections state
  const [sections, setSections] = useState<DialogSection[]>([]);
  const [sectionsVersion, setSectionsVersion] = useState(0);
  const [dialogVariables, setDialogVariables] = useState<DialogVariable[]>([]);
  
  // Flow state
  const [localFlowSections, setLocalFlowSections] = useState<DialogSection[]>([]);
  const [flowInitialized, setFlowInitialized] = useState(false);
  const [flowId, setFlowId] = useState<string | null>(null);
  const [flowNodes, setFlowNodes] = useState<Node[]>([]);
  const [flowEdges, setFlowEdges] = useState<Edge[]>([]);
  const [flowViewport, setFlowViewport] = useState<{ x: number; y: number; zoom: number } | null>(null);
  const [isLoadingFlow, setIsLoadingFlow] = useState(false);
  const [needsFlowCreation, setNeedsFlowCreation] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('flow');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    show: false,
    position: { x: 0, y: 0 },
  });
  
  // Dialog jump selector state
  const [dialogJumpSelectorOpen, setDialogJumpSelectorOpen] = useState(false);
  const [pendingJumpConnection, setPendingJumpConnection] = useState<PendingJumpConnection | null>(null);
  
  // Refs for accessing latest state
  const sectionsRef = useRef(sections);
  const localFlowSectionsRef = useRef(localFlowSections);
  const flowNodesRef = useRef(flowNodes);
  const prevViewModeRef = useRef(viewMode);
  const regenerateFlowFromSectionsRef = useRef<(() => void) | undefined>();
  
  // Keep refs up to date
  useEffect(() => {
    sectionsRef.current = sections;
    localFlowSectionsRef.current = localFlowSections;
    flowNodesRef.current = flowNodes;
  });
  
  // Computed values
  const effectiveGameId = gameId || initialData?.game_id || '';
  
  const sectionIdentifiers = useMemo(() => {
    return sections.map((s) => s.section_identifier);
  }, [sections]);
  
  return {
    // Dialog metadata
    dialogName,
    setDialogName,
    dialogKey,
    setDialogKey,
    description,
    setDescription,
    translatable,
    setTranslatable,
    startingSection,
    setStartingSection,
    tags,
    setTags,
    status,
    setStatus,
    owner,
    setOwner,
    editPermissions,
    setEditPermissions,
    
    // Speaker configuration
    speakerMode,
    setSpeakerMode,
    speakerTypeId,
    setSpeakerTypeId,
    speakerTemplateIds,
    setSpeakerTemplateIds,
    
    // Sections state
    sections,
    setSections,
    sectionsVersion,
    setSectionsVersion,
    dialogVariables,
    setDialogVariables,
    
    // Flow state
    localFlowSections,
    setLocalFlowSections,
    flowInitialized,
    setFlowInitialized,
    flowId,
    setFlowId,
    flowNodes,
    setFlowNodes,
    flowEdges,
    setFlowEdges,
    flowViewport,
    setFlowViewport,
    isLoadingFlow,
    setIsLoadingFlow,
    needsFlowCreation,
    setNeedsFlowCreation,
    isDataLoaded,
    setIsDataLoaded,
    
    // UI state
    viewMode,
    setViewMode,
    currentSectionIndex,
    setCurrentSectionIndex,
    isSubmitting,
    setIsSubmitting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    
    // Context menu state
    contextMenu,
    setContextMenu,
    
    // Dialog jump selector state
    dialogJumpSelectorOpen,
    setDialogJumpSelectorOpen,
    pendingJumpConnection,
    setPendingJumpConnection,
    
    // Refs
    sectionsRef,
    localFlowSectionsRef,
    flowNodesRef,
    prevViewModeRef,
    regenerateFlowFromSectionsRef,
    
    // Computed values
    effectiveGameId,
    sectionIdentifiers,
  };
}

